import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  Box, Container, Typography, Paper, Grid, Divider, 
  Button, Avatar, Stack, Chip, CircularProgress, Alert 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WcIcon from '@mui/icons-material/Wc';
import BadgeIcon from '@mui/icons-material/Badge';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Navbar from './Navbar';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const docRef = doc(db, "users", userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setUserData(docSnap.data());
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to update status (Admin Action)
  const handleStatusUpdate = async (newStatus) => {
    if (!window.confirm(`Are you sure you want to set this user to ${newStatus}?`)) return;
    
    setUpdating(true);
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        status: newStatus,
        // If approving to premium, you might want to set an expiry date here too
      });
      alert(`User status updated to ${newStatus}`);
      fetchUser(); // Refresh data
    } catch (error) {
      console.error("Update failed:", error);
      alert("Failed to update status.");
    } finally {
      setUpdating(false);
    }
  };

  const calculateAge = (dobString) => {
    if (!dobString) return 'N/A';
    const birthDate = new Date(dobString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    return age > 0 ? `${age} years` : 'N/A';
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!userData) return <Container sx={{ mt: 10 }}><Typography>User not found.</Typography></Container>;

  return (
    <Box sx={{ py: 8, bgcolor: '#f4f7fb', minHeight: '100vh' }}>
      <Navbar />
      <Container sx={{ my: 5 }} maxWidth="md">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin')} sx={{ mb: 4, color: '#666' }}>
          Back to User List
        </Button>

        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: '30px', boxShadow: '0 10px 40px rgba(0,0,0,0.04)' }}>
          {/* Header */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} alignItems="center" mb={5}>
            <Avatar src={userData.profilePhoto} sx={{ width: 120, height: 120, bgcolor: '#B200BB' }} />
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h4" fontWeight={800}>{userData.firstName + ' ' + userData.lastName || 'Unnamed User'}</Typography>
              <Typography color="text.secondary">{userData.email}</Typography>
              <Stack direction="row" spacing={1} mt={2} justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                <Chip label={userData.status?.toUpperCase()} color={userData.status === 'premium' ? 'success' : 'warning'} />
                <Chip label={userData.role} variant="outlined" />
              </Stack>
            </Box>
          </Stack>

          <Divider sx={{ mb: 5 }} />

          {/* Verification Actions */}
          <Box sx={{ mb: 5, p: 3, bgcolor: '#FFF9C4', borderRadius: '15px', border: '1px solid #FBC02D' }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <VerifiedUserIcon sx={{ color: '#F57F17' }} />
              <Typography variant="h6" fontWeight={700}>Admin Verification Actions</Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button 
                variant="contained" 
                color="success" 
                disabled={updating}
                onClick={() => handleStatusUpdate('premium')}
              >
                Approve (Premium)
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                disabled={updating}
                onClick={() => handleStatusUpdate('rejected')}
              >
                Reject
              </Button>
              <Button 
                variant="outlined" 
                disabled={updating}
                onClick={() => handleStatusUpdate('trial')}
              >
                Reset to Trial
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={5}>
            {/* Personal Details */}
            <Grid item xs={12} md={6}>
              <SectionTitle icon={<WcIcon fontSize="small" />} title="Profile Details" />
              <InfoField label="Gender" value={userData.sex} />
              <InfoField label="Date of Birth" value={userData.dob} />
              <InfoField label="Age" value={calculateAge(userData.dob)} />
            </Grid>

            {/* Account Info */}
            <Grid item xs={12} md={6}>
              <SectionTitle icon={<BadgeIcon fontSize="small" />} title="Identity Verification" />
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                NIN / ID Document
              </Typography>
              {userData.ninUrl ? (
                <Box 
                  component="img" 
                  src={userData.ninUrl} 
                  alt="NIN Document"
                  sx={{ 
                    width: '100%', 
                    borderRadius: '10px', 
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 } 
                  }}
                  onClick={() => window.open(userData.ninUrl, '_blank')}
                />
              ) : (
                <Alert severity="warning">No ID document uploaded.</Alert>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

// Sub-components
const SectionTitle = ({ icon, title, sx }) => (
  <Stack direction="row" spacing={1} alignItems="center" mb={2} sx={sx}>
    <Box sx={{ color: '#B200BB', display: 'flex' }}>{icon}</Box>
    <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
  </Stack>
);

const InfoField = ({ label, value }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{label}</Typography>
    <Typography variant="body1" fontWeight={500}>{value || 'Not provided'}</Typography>
  </Box>
);

export default UserDetails;